/*******************************************************************************
 * Copyright (c) 2024 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {APP_CONFIG} from '@common-core/config/app-config';
import {EnvironmentConfig} from '@shared/models/environment-config';
import {Observable} from 'rxjs';
import {PresignedUrlResponse} from '@shared/models/presigned-url';
import {X_PSP_TYPE} from '@common-core/interceptors/auth/x-psp-type';
import {PspRole} from '@shared/models/psp-role';
import {NgxRolesService} from 'ngx-permissions';

@Injectable()
export class PresignedUrlService {
  private httpClient = inject(HttpClient);
  private appConfig = inject<EnvironmentConfig>(APP_CONFIG);
  private rolesService = inject(NgxRolesService);

  /**
   * Get request options with headers based on roles
   * @param assumeIdentity - assume identity
   * @param additionalOptions - Additional options such as params
   * @param additionalOptions.params - Additional params
   * @returns - Request options
   */
  private getRequestOptions(
    assumeIdentity: boolean = true,
    additionalOptions?: {params?: any}
  ): {
    headers?: HttpHeaders | {[header: string]: string | string[]};
  } {
    let requestOptions: {
      headers?: HttpHeaders | {[header: string]: string | string[]};
      params?: any;
    } = {
      ...additionalOptions,
    };

    if (
      (!!this.rolesService.getRole(PspRole.INTEGRATOR_ADMIN) ||
        !!this.rolesService.getRole(PspRole.INTEGRATOR_SUPPORT)) &&
      assumeIdentity
    ) {
      requestOptions.headers = {'X-PSP-Type': X_PSP_TYPE.integratorManageFinancialEntity};
    }

    return requestOptions;
  }

  /**
   * Post request to get presigned url.
   * Post request to get presigned url with the FormData with the fields and file appended.
   * @param root - The root objects
   * @param root.bulkImportResponse - The bulk import response
   * @param root.csvFile - The csv file
   * @returns Observable<HttpResponse<void>> - The response
   */
  postToPresignedUrl({
    bulkImportResponse,
    csvFile,
  }: {
    bulkImportResponse: PresignedUrlResponse;
    csvFile: string;
  }): Observable<HttpResponse<void>> {
    // Create a new FormData object
    const formData = new FormData();

    // Append the fields to the form data
    Object.keys(bulkImportResponse.fields).forEach(key => {
      formData.append(key, bulkImportResponse.fields[key]);
    });

    // Append the file to the form data
    const file = new File([csvFile], 'file', {type: 'text/plain'});
    formData.append('file', file);

    return this.httpClient.post<void>(bulkImportResponse.presignedUrl, formData, {
      observe: 'response',
      headers: {
        'X-PSP-Type': X_PSP_TYPE.noAuhtorizationHeader,
      },
    });
  }

  /**
   * Endpoint to notify CORE that a file has been succesfully imported
   * to store it in the DB
   * @param fileName - the uploaded file name
   * @returns Observable<HttpResponse<void>> - No response
   */
  notifyUpload(fileName: string): Observable<HttpResponse<void>> {
    return this.httpClient.post<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}/gmf/import/notify-upload`,
      {
        file_name: fileName,
      },
      this.getRequestOptions()
    );
  }
}
